import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle2, ChevronRight, Sparkles, ArrowRight,
  ShieldAlert, Scale, TrendingUp, AlertTriangle,
  Users, Activity, Workflow, Eye, Database, Network, Lock,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";
import useNoIndex from "@/lib/useNoIndex";
import BespokePageRibbon from "@/components/BespokePageRibbon";
import BespokeWatermark from "@/components/BespokeWatermark";

// Phase 25.16m — Dabble bespoke proposal landing page.
//
// Trigger: Shaun Hill (Ventia contact) offered to introduce the founder
// to Jon Robin (co-founder, Dabble — AU social bookmaker, NTRWC-licensed).
// This page is the bespoke ABM landing the introduction email will point to.
//
// Same architectural pattern as /ventia and /strategic-planning-institute.
// Sector cards are RE-VOICED for the sports-betting operating context:
// trading desk · AML/AUSTRAC · NCPF responsible gambling · BetStop ·
// social-graph integrity · match-day surge ops · customer LTV.
//
// Logo-free by design — "DABBLE × DARA OS" text mark avoids trademark
// issues and reinforces the bespoke-proposal feel.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Six operating-context capability blocks. Re-voiced in Phase 25.16m
// to lead with the social-USP-as-moat angle: Dabble's competitive moat
// is the social product, but that moat is only defensible if the
// regulator-grade audit trail backs it up — DARA does both at once.
const CAPABILITIES = [
  {
    Icon: Users,
    code: "01",
    name: "Social-USP moat · regulator-defensible by design",
    plain: "Every time someone copies a bet, follows a tipster, or shares a tip, we keep a record of who, when, and why. If a regulator ever asks 'how do you know your social product isn't being used for match-fixing or coordinated activity?', you can show them, in seconds. That's it.",
    mvp: "Every copy-bet cascade traced. Every tipster risk-scored. Every social signal audit-tagged. Coordinated inauthentic behaviour (CIB) detection across follower networks. Tipster-concentration risk tied directly to book exposure. Pre-built integrity-body reporting workflow (Tennis IU, AFL/NRL integrity, ACMA). This is what makes the social product safe to scale — and what stops Sportsbet/Bet365 from credibly cloning it.",
    roadmap: "Cross-bookmaker collusion signal exchange (industry-grade, anonymised). Predictive harm-amplification scoring across social cascades. Tipster-economy ROI vs concentration-risk dashboard.",
    proof: "The social product is the moat. DARA makes the moat regulator-grade. Without it, the regulator angle eventually closes the social product down — for everyone.",
  },
  {
    Icon: TrendingUp,
    code: "02",
    name: "Tipster economy intelligence · the engine inside the engine",
    plain: "We tell you which tipsters are actually making you money, which ones are about to leave for Sportsbet, and which ones are sitting on dangerous concentration risk. So you stop guessing who to retain — and the 5% of tipsters who drive 60% of your GMV are kept happy without dropping a dollar on the wrong 95%.",
    mvp: "The tipster economy IS Dabble's flywheel. DARA models it explicitly: which tipsters drive secondary GMV, which create concentration risk, which trigger churn cascades, which are inflating performance through social-graph manipulation. Per-tipster contribution-margin reporting. Cohort revenue by archetype (casual vs serious vs tipster vs follower). 30-day churn-signal detection across the graph.",
    roadmap: "Per-tipster cost-of-acquisition vs lifetime social-graph value (not just direct revenue). Tipster-stratified retention strategy. Marketing attribution beyond last-touch.",
    proof: "The difference between $10M ARR and $40M ARR isn't more customers — it's understanding which 5% of tipsters drive 60% of GMV, and not losing them to a single misjudged intervention.",
  },
  {
    Icon: TrendingUp,
    code: "03",
    name: "Real-time book liability reconciliation",
    plain: "We watch your live odds and your risk system side by side, and within seconds tell you if a single market is leaning too one-sided. Your trading desk gets the signal BEFORE the Grand Final blows up your week — not after.",
    mvp: "Read-only ingest of internal odds engine + risk engine + market-by-market liability ladder. Sub-second anomaly flags when a market drifts beyond pre-set bands, before exposure breach. Cross-references against exchange (Betfair) layoff opportunities in the same dashboard. Concentration alerts when the social graph stacks one-way exposure on a single fixture.",
    roadmap: "Auto-layoff recommendation engine. Real-time per-tipster contribution to book exposure (closing the loop with capability 02).",
    proof: "One avoided exposure breach on a major fixture (NRL Grand Final, Melbourne Cup, AFL final) = direct P&L protection in the $100k-$500k band per event.",
  },
  {
    Icon: Scale,
    code: "04",
    name: "AUSTRAC AML/CTF intelligence layer",
    plain: "Every morning we hand your compliance lead a list of the 3 customer cases they need to look at TODAY to stay on top of AUSTRAC obligations. No more 'we'll get to it' that ends in a missed filing window. The MLRO walks into the day knowing exactly what's on the desk.",
    mvp: "Read-only ingest from existing AML platform (Senet, ComplyAdvantage, Quantexa). Daily SMR/TTR pre-flight brief — &quot;3 cases the MLRO should look at before close.&quot; Deposit-bet-withdraw cycle detection, structured-deposit signature flags, source-of-funds anomaly correlation. AML signal correlation against social-graph cascade activity.",
    roadmap: "Pre-baked AUSTRAC reporting templates. Banking-pattern signal feeds. Automated 'enhanced customer due diligence' (ECDD) trigger.",
    proof: "One missed SMR filing window = AUSTRAC infringement. One pattern-of-failure = enforcement action. The downside is asymmetric — DARA inverts it.",
  },
  {
    Icon: ShieldAlert,
    code: "05",
    name: "NCPF responsible-gambling intervention",
    plain: "We spot customers who are starting to show problem-gambling patterns 30 days BEFORE it becomes a crisis. Your team gets a list of who to reach out to and what to say — quietly, gently, before regulators or media notice. Saves customers, saves brand, saves licence.",
    mvp: "National Consumer Protection Framework (NCPF) signal detection: chasing-loss pattern, session-length runaway, deposit velocity, time-of-day risk score, social-cascade-induced behavioural shifts. Intervention timing: automated nudge → manual outreach → mandatory deposit-limit conversation. BetStop register cross-reference latency monitoring with fail-closed alerting.",
    roadmap: "Predictive harm-onset scoring (next 30 days). Cohort-level NCPF compliance reporting for state regulators. Cascade-aware intervention (when a tipster's loss-chase pattern is spreading through copy-bets).",
    proof: "An ACMA action over inadequate responsible-gambling intervention is a brand-defining event for a challenger bookmaker. Pre-empting it is existential.",
  },
  {
    Icon: Activity,
    code: "06",
    name: "Match-day ops orchestration",
    plain: "When 50,000 people are placing bets at the same time on Friday Night NRL, you'll know within seconds if anything is slowing down or going sideways. The COO has one screen to look at, not eight. Surge, monitored. Customer service, ready. Marketing spend, tracked.",
    mvp: "Friday night NRL, Saturday AFL multi-match, Tuesday A-League — DARA treats match-day like an industrial control room. Bet acceptance latency SLA, app perf degradation alerts, customer-service surge prediction, marketing spend ROI per fixture, payment processor health, ISP-side outage cross-reference. Social cascade velocity overlaid on book exposure as the match unfolds.",
    roadmap: "Predictive surge model (bet volume by match × hour × competing-fixture). Auto-scaling triggers tied to the same model. Cascade velocity → market-suspension recommendation when concentration risk crosses threshold.",
    proof: "Slow bet acceptance during a major fixture = customer churn signal AND ACMA service-standard implication. DARA gives the COO a single screen during the surge.",
  },
];

const SOVEREIGNTY = [
  { Icon: CheckCircle2, label: "AU-sovereign deployment. Data resident in AWS Sydney or Azure Australia Central. No offshore data flow.", colour: "text-[#00E676]" },
  { Icon: CheckCircle2, label: "Read-only ingest. DARA reads from your existing odds engine, risk engine, AML platform, and social graph. No writes. No actuation. Your system-of-record stays exactly where it is.", colour: "text-[#00E676]" },
  { Icon: CheckCircle2, label: "Tenant-isolated. Per-tenant database. AES-256 at rest. TLS 1.3 in transit. Audit log every read.", colour: "text-[#00E676]" },
  { Icon: CheckCircle2, label: "Regulator-grade audit trail. Every brief, every flag, every intervention — auditable end-to-end against ACMA, AUSTRAC, NCPF.", colour: "text-[#00E676]" },
];

export default function Dabble() {
  usePageview("/dabble");
  useNoIndex();

  const [form, setForm] = useState({
    email: "", name: "", role: "", focus: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Work email is required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        email: form.email,
        name: form.name,
        company: "Dabble",
        role: form.role,
        message: [
          form.focus ? `Focus: ${form.focus}` : "",
          form.message ? `Notes: ${form.message}` : "",
        ].filter(Boolean).join("\n"),
        industry: "dabble",
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("Received — DARA founder will reach out within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="dabble-page">
      <BespokePageRibbon prospectName="Dabble · AU Social Bookmaker" />
      <BespokeWatermark pageLabel="DABBLE" position="top" />

      {/* Top nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="dabble-back-to-home">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-2">
              · DABBLE · BESPOKE PROPOSAL
            </span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <a href="#engage" data-testid="dabble-nav-engage" className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90">
              Continue the conversation
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFB000]/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4">
            // BESPOKE PROPOSAL · PREPARED FOR DABBLE · AU SOCIAL BOOKMAKER · MAY 2026
          </div>
          <div className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-zinc-500 mb-3">
            DABBLE × DARA OS
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Dabble's social product is your <span className="text-[#FFB000]">USP</span>. <span className="text-[#FFB000]">DARA</span> makes it the moat the incumbents can't copy.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            Dabble is the most interesting social bookmaker in Australia. $0 → $10M ARR. Brisbane-built. NTRWC-licensed. Genuinely Australian-owned. Your social product — copy-bet cascades, tipster economy, follower graph — is what Sportsbet, Ladbrokes, and Bet365 can't copy. <b>Not because they lack the tech. Because they can't defend it to AUSTRAC.</b>
          </p>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            That's where <b>DARA OS</b> sits: the intelligence layer between your bet book, your social graph, and every regulator who'll eventually audit it. Every copy-bet cascade traced. Every tipster risk-scored. Every social signal audit-tagged. So every AUSTRAC review, ACMA service-standard audit, or NCPF intervention spot-check becomes a <b>5-minute answer</b> instead of a 6-week scramble — and your social product stays the moat the incumbents structurally can't replicate.
          </p>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <a
              href="#engage"
              data-testid="dabble-hero-engage-cta"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              <ChevronRight className="w-4 h-4" /> Schedule the follow-up <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/dabble/live"
              data-testid="dabble-hero-live-demo-cta"
              className="inline-flex items-center gap-2 border border-[#FFB000] text-[#FFB000] font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-[#FFB000]/10"
            >
              <Eye className="w-4 h-4" /> See your morning brief
            </Link>
            <a
              href="#capabilities"
              data-testid="dabble-hero-capabilities-link"
              className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] underline-offset-4 hover:underline flex items-center gap-1.5"
            >
              <Workflow className="w-3.5 h-3.5" /> Skip to the 6-capability map
            </a>
          </div>
        </div>
      </section>

      {/* THE WEDGE */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // THE WEDGE · IN ONE PARAGRAPH
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            DARA does <span className="text-[#FFB000]">two things at once</span>. First — every regulator visit becomes a <span className="text-[#FFB000]">5-minute answer</span>. Second — and more important — Dabble's social product becomes <span className="text-[#FFB000]">regulator-defensible end-to-end</span>. That's the moat.
          </h2>
          <p className="mt-6 text-sm sm:text-base text-zinc-300 max-w-4xl leading-relaxed">
            The incumbents can't copy Dabble's social product not because of the tech, but because they can't defend it to AUSTRAC. Every cascade traced. Every tipster risk-scored. Every social signal audit-tagged. Same architectural pattern DARA already runs across AU utilities and Tier-1 industrial services — the connectors swap (odds engines, AML platforms, social-graph DBs instead of SCADA and CMMS), the shape of the problem doesn't: dozens of independent feeds, reconciliation across them, regulator-grade audit trail, daily brief that catches what humans miss.
          </p>
        </div>
      </section>

      {/* CAPABILITIES GRID */}
      <section id="capabilities" className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// SIX CAPABILITIES · ORDERED BY MOAT IMPACT</div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">
            What DARA actually does &nbsp;<span className="text-zinc-500 font-mono text-base font-normal">— starting with what makes the social product defensible</span>
          </h2>
          <p className="mt-3 text-sm text-zinc-400 max-w-3xl">
            Each capability ships with an MVP that's deployable in 8-12 weeks, a 3-6 month roadmap, and a business case calibrated to the realities of the AU wagering market.
          </p>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="dabble-capabilities-grid">
            {CAPABILITIES.map(({ Icon, code, name, plain, mvp, roadmap, proof }) => (
              <div key={code} className="bg-black p-6 sm:p-7 flex flex-col" data-testid={`dabble-cap-${code}`}>
                <div className="flex items-center gap-3 mb-3">
                  <Icon className="w-5 h-5 text-[#FFB000]" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// CAPABILITY {code}</span>
                </div>
                <h3 className="font-heading font-bold text-lg tracking-tight leading-tight">{name}</h3>
                {/* Phase 25.16n — Plain-English lead. Sits above the
                    technical MVP/Roadmap so a non-engineering founder
                    reads value in one breath. */}
                {plain && (
                  <div className="mt-4 border-l-2 border-[#FFB000] pl-3 py-1 bg-[#FFB000]/5">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mb-1">▸ IN PLAIN ENGLISH</div>
                    <p className="text-sm text-zinc-100 leading-relaxed">{plain}</p>
                  </div>
                )}
                <div className="mt-4 space-y-3 flex-1">
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#00E676] mb-1">▸ MVP · LIVE IN 8-12 WEEKS</div>
                    <p className="text-xs text-zinc-400 leading-relaxed">{mvp}</p>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mb-1">▸ ROADMAP · 3-6 MONTHS</div>
                    <p className="text-xs text-zinc-500 leading-relaxed">{roadmap}</p>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">▸ BUSINESS CASE</div>
                    <p className="text-xs text-zinc-500 leading-relaxed italic">{proof}</p>
                  </div>
                </div>
                <Link
                  to="/dabble/live"
                  data-testid={`dabble-cap-${code}-demo-link`}
                  className="mt-5 inline-flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] border-t border-zinc-900 pt-3 group"
                >
                  <span className="flex items-center gap-1.5">
                    <Eye className="w-3 h-3" /> See this in your morning brief
                  </span>
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ARCHITECTURE / TRUST */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// ARCHITECTURE · WHY THE CTO WILL NOD</div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl">
            <span className="text-[#FFB000]">Read-only</span>, AU-sovereign, tenant-isolated, regulator-auditable.
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {SOVEREIGNTY.map(({ Icon, label, colour }, i) => (
              <div key={i} className="flex items-start gap-3 bg-black border border-zinc-900 p-5 sm:p-6">
                <Icon className={`w-5 h-5 ${colour} flex-shrink-0 mt-0.5`} />
                <p className="text-sm text-zinc-200 leading-relaxed">{label}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 border-t border-zinc-900 pt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <ArchBlock
              Icon={Database}
              label="INGEST"
              body="Odds engine · Risk engine · AML platform (Senet, ComplyAdvantage, Quantexa) · Social graph DB · App perf monitor · BetStop API · Payment processors · Marketing platform"
            />
            <ArchBlock
              Icon={Network}
              label="DARA OS"
              body="Reconciliation across feeds · Anomaly classifier · Bespoke sportsbook narrator · Daily ops brief · Regulator pack template · Audit log"
              accent
            />
            <ArchBlock
              Icon={Lock}
              label="DELIVERY"
              body="Trading desk dashboard · MLRO / Compliance brief · Responsible-gambling intervention queue · Integrity-body reporting · ACMA / AUSTRAC pre-flight"
            />
          </div>
        </div>
      </section>

      {/* COMMERCIAL */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// COMMERCIAL · THREE STRUCTURES</div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">Pick the deal model that matches your phase.</h2>
          <p className="mt-3 text-sm text-zinc-400 max-w-3xl">
            DARA flexes to operating reality. The three structures below cover most engagements at Dabble's scale; bespoke structures available on the call.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            <DealCard
              tag="LIGHTHOUSE PILOT"
              price="$0 fee · 12 weeks · 1 capability"
              body="DARA picks up the cost of the first pilot in exchange for a published case study + 2 reference calls + first-look on the expansion. Best for the &quot;prove it before procurement&quot; phase."
            />
            <DealCard
              tag="STANDARD"
              price="A$15-25k / month · per capability · annual"
              body="Production deployment across one capability (e.g. AUSTRAC AML layer or NCPF intervention queue) with quarterly reviews and continuous improvement of the brief vocabulary. Standard MNDA + SaaS terms."
              highlight
            />
            <DealCard
              tag="OPERATING PARTNER"
              price="A$50-90k / month · all-capabilities · 24-mo"
              body="Full DARA deployment across all six capability blocks. Dedicated DARA engineer embedded with Dabble trading + compliance teams. Quarterly co-developed roadmap. Source-available licence."
            />
          </div>
        </div>
      </section>

      {/* ENGAGE FORM */}
      <section id="engage" className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// CONTINUE THE CONVERSATION</div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">Pick one capability. 12 weeks. $0 fee.</h2>
          <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
            Submit details and DARA's founder will reach out within one business day with two proposed pilot capabilities scoped to Dabble's current operating reality, plus a draft mutual NDA for the first pilot. No procurement engagement until both sides see signal.
          </p>

          {submitted ? (
            <div className="mt-8 border border-[#00E676]/30 bg-[#00E676]/5 p-6">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-[#00E676]" />
                <span className="font-mono text-xs uppercase tracking-widest text-[#00E676]">RECEIVED</span>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed">
                Thanks — DARA's founder will reach out within one business day with a proposed pilot scope. In the meantime, your morning brief preview is at <Link to="/dabble/live" className="text-[#FFB000] underline-offset-2 hover:underline">daraos.ai/dabble/live</Link>.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5 border border-zinc-900 bg-black p-6 sm:p-8" data-testid="dabble-engage-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input label="WORK EMAIL *" value={form.email} onChange={(v) => setForm(f => ({ ...f, email: v }))} type="email" required testid="dabble-form-email" />
                <Input label="FULL NAME" value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} testid="dabble-form-name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input label="ROLE / TITLE" value={form.role} onChange={(v) => setForm(f => ({ ...f, role: v }))} testid="dabble-form-role" />
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">CAPABILITY OF IMMEDIATE INTEREST</span>
                  <select
                    value={form.focus}
                    onChange={(e) => setForm(f => ({ ...f, focus: e.target.value }))}
                    data-testid="dabble-form-focus"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  >
                    <option value="">— Pick one —</option>
                    <option value="social-moat">01 · Social-USP moat (regulator-defensible)</option>
                    <option value="tipster">02 · Tipster economy intelligence</option>
                    <option value="liability">03 · Liability reconciliation</option>
                    <option value="austrac">04 · AUSTRAC AML/CTF</option>
                    <option value="ncpf">05 · NCPF responsible gambling</option>
                    <option value="matchday">06 · Match-day ops orchestration</option>
                    <option value="all">All six · Operating Partner</option>
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">ANYTHING SPECIFIC YOU WANT FLAGGED BEFORE THE NEXT CALL (OPTIONAL)</span>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                  data-testid="dabble-form-message"
                  rows={4}
                  className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                />
              </label>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <p className="text-[10px] text-zinc-600 font-mono leading-relaxed">
                  By submitting you accept that DARA will contact you about this<br />opportunity. Mutual NDA available pre-pilot.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="dabble-form-submit"
                  className="bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <ChevronRight className="w-4 h-4" />
                  {submitting ? "Sending..." : "Send to DARA founder"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* RISK / HONESTY BLOCK */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" />// HONESTLY · WHERE DARA ISN'T THE FIT YET
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <RiskItem text="DARA is not an odds-pricing engine. We sit ABOVE your odds engine; we don't replace it." />
            <RiskItem text="DARA does not write to your platform. Pure read-only. If you want auto-suspension of a market on a flag, your engineering team builds that hook against our brief output." />
            <RiskItem text="DARA is pre-seed. Reference customers in wagering aren't yet public. Reference calls with active industrial-services design partners are available under NDA." />
          </ul>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Documents (under NDA)</div>
            <ul className="space-y-2">
              <li><span className="text-[11px] text-zinc-400">DABBLE × DARA · 1-page intro</span></li>
              <li><span className="text-[11px] text-zinc-400">DARA · Reference &amp; Proof Points</span></li>
              <li><span className="text-[11px] text-zinc-400">DARA · Mutual NDA template</span></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Trust</div>
            <ul className="space-y-2">
              <li><Link to="/dabble/live" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">See your morning brief</Link></li>
              <li><Link to="/industrial-demo?profile=sportsbook" className="text-[11px] text-zinc-500 hover:text-[#FFB000]">Engineering view · live telemetry</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">DARA OS</div>
            <p className="text-[11px] text-zinc-600 leading-relaxed">
              The Bloomberg-terminal for enterprise operators. Built in Australia, deployed worldwide.
            </p>
            <p className="text-[10px] text-zinc-700 leading-relaxed mt-3 font-mono">
              Bespoke proposal · prepared for Dabble · not for redistribution outside Dabble leadership without DARA permission.
            </p>
          </div>
        </div>
        <div className="border-t border-zinc-900 px-4 sm:px-6 py-4 max-w-7xl mx-auto flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">© DARA OS · {new Date().getFullYear()}</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-700">DABBLE × DARA · v1</span>
        </div>
      </footer>
      <BespokeWatermark pageLabel="DABBLE" position="bottom" />
    </div>
  );
}

function ArchBlock({ Icon, label, body, accent = false }) {
  return (
    <div className={`p-5 ${accent ? "border border-[#FFB000]/40 bg-[#FFB000]/5" : "border border-zinc-900"}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${accent ? "text-[#FFB000]" : "text-zinc-400"}`} />
        <span className={`font-mono text-[10px] uppercase tracking-widest ${accent ? "text-[#FFB000]" : "text-zinc-500"}`}>// {label}</span>
      </div>
      <p className="text-xs text-zinc-300 leading-relaxed">{body}</p>
    </div>
  );
}

function DealCard({ tag, price, body, highlight = false }) {
  return (
    <div className={`p-6 ${highlight ? "bg-[#FFB000]/10 border-l-2 border-[#FFB000]" : "bg-black"}`}>
      <div className={`font-mono text-[10px] uppercase tracking-widest ${highlight ? "text-[#FFB000]" : "text-zinc-500"} mb-2`}>// {tag}</div>
      <div className="font-heading font-bold text-lg tracking-tight mb-3">{price}</div>
      <p className="text-xs text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function RiskItem({ text }) {
  return (
    <li className="border border-zinc-900 p-4 text-xs text-zinc-400 leading-relaxed">{text}</li>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}
